import { BootstrapIcon, getBootstrapIconUrl } from '../Global/icons'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '../Global/loadDependency'
import { getNavEnterpriseStyles } from './NavEnterprise.styles'
import type { } from './types/NavEnterprise.type'

const iconLeft = getBootstrapIconUrl(BootstrapIcon.ChevronLeft)

const t = document.createElement('template')
t.innerHTML = `
  <nav class="nav-enterprise-container">
    <div class="logo-container">
      <slot name="logo-expanded" class="logo-expanded"></slot>
      <slot name="logo-collapsed" class="logo-collapsed">
        <img class="logo-collapsed-default" src="/src/assets/images/TruStage-Logo-Icon.svg" alt="TruStage" />
      </slot>
      <slot name="logo" class="logo-single"></slot>
      <slot name="logo-actions" class="logo-actions"></slot>
    </div>
    <div class="nav-top">
      <slot></slot>
    </div>
    <div class="nav-bottom">
      <slot name="footer"></slot>
      <wa-button
        size="small"
        appearance="outlined"
        class="collapse-toggle"
        aria-label="Click to Close"
        aria-expanded="true"
      >
        <wa-icon slot="start" src="${iconLeft}"></wa-icon> <span></span>
      </wa-button>
    </div>
  </nav>
`
const TemplateNavEnterprise = t

class NavEnterprise extends HTMLElement {
  private isCollapsed = false
  private toggleHandler: (() => void) | null = null

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Button, WebAwesomeComponent.Icon])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getNavEnterpriseStyles().content.cloneNode(true))
    shadow.appendChild(TemplateNavEnterprise.content.cloneNode(true))

    tryLoadLightDOMStyles('NavEnterprise.css')
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavEnterprise connectedCallback')

    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    if (toggleButton) {
      this.toggleHandler = () => this.toggleCollapse()
      toggleButton.addEventListener('click', this.toggleHandler)
    }

    this.addEventListener('enterprise-nav-close', this.onCloseBtnClick)

    const collapsedAttr = this.getAttribute('collapsed')
    if (collapsedAttr !== null) {
      this.setCollapsed(collapsedAttr === 'true')
    } else {
      const persistedCollapsed = localStorage.getItem('nav-enterprise-collapsed')
      this.setCollapsed(persistedCollapsed === 'true')
    }

    this.updateMobileMenuVisibility()
  }

  disconnectedCallback() {
    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    if (toggleButton && this.toggleHandler) {
      toggleButton.removeEventListener('click', this.toggleHandler)
      this.toggleHandler = null
    }
    this.removeEventListener('enterprise-nav-close', this.onCloseBtnClick)
  }

  private onCloseBtnClick = (event: Event) => {
    if ((event as CustomEvent).type === 'enterprise-nav-close') {
      this.removeAttribute('enterprise-nav-open')
    }
  }

  toggleCollapse() {
    this.setCollapsed(!this.isCollapsed)
  }

  setCollapsed(collapsed: boolean) {
    this.isCollapsed = collapsed
    const navContainer = this.shadowRoot?.querySelector('.nav-enterprise-container')
    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    const icon = toggleButton?.querySelector('wa-icon')
    const span = toggleButton?.querySelector('span')
    const label = collapsed ? 'Expand Sidebar' : 'Click to Close'

    if (navContainer) {
      navContainer.classList.toggle('collapsed', collapsed)
    }

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', String(!collapsed))
      toggleButton.setAttribute('aria-label', label)
    }

    if (icon) {
      icon.setAttribute('name', collapsed ? 'chevron-right' : 'chevron-left')
    }

    if (span) {
      span.textContent = label
    }

    this.classList.toggle('collapsed', collapsed)
    localStorage.setItem('nav-enterprise-collapsed', String(collapsed))

    this.dispatchEvent(
      new CustomEvent('nav-collapse-change', {
        detail: { collapsed },
        bubbles: true,
        composed: true,
      }),
    )
  }

  updateMobileMenuVisibility() {
    const shadow = this.shadowRoot
    if (!shadow) return

    const container = shadow.querySelector('.nav-enterprise-container') as HTMLElement | null
    if (!container) return

    const isOpen = this.hasAttribute('enterprise-nav-open')
    container.classList.toggle('enterprise-nav-open', isOpen)
  }

  static get observedAttributes() {
    return ['collapsed', 'enterprise-nav-open']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'collapsed') {
      this.setCollapsed(newValue === 'true')
    } else if (name === 'enterprise-nav-open') {
      this.updateMobileMenuVisibility()
    }
  }
}

if (!customElements.get('ts-nav-enterprise')) {
  customElements.define('ts-nav-enterprise', NavEnterprise)
}
export { NavEnterprise }
